import { useEffect, useState } from "react";

import DashboardShell from "../components/common/DashboardShell";
import { LeagueApi } from "../utils/leagueApi";

// Actions available from each invite status (state machine).
const NEXT_ACTIONS = {
  applied: ["approve", "decline"],
  approved: ["confirm", "decline"],
  confirmed: ["lock", "decline"],
  locked: [],
  declined: [],
};

export default function FreeAgentHub() {
  const [status, setStatus] = useState({ is_free_agent: false, headline: "" });
  const [agents, setAgents] = useState([]);
  const [invites, setInvites] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [me, list, inv, chal] = await Promise.all([
      LeagueApi.getMyStatus(),
      LeagueApi.listFreeAgents(),
      LeagueApi.listInvites(),
      LeagueApi.myChallenges(),
    ]);
    setStatus({ is_free_agent: me.is_free_agent, headline: me.headline || "" });
    setAgents(list);
    setInvites(inv);
    setChallenges(chal);
  };

  useEffect(() => {
    load()
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, []);

  const reload = () => {
    load().catch(() => undefined);
  };

  const saveStatus = async () => {
    await LeagueApi.setMyStatus(status);
    reload();
  };

  const advance = async (id, action) => {
    await LeagueApi.advanceInvite(id, action).catch(() => undefined);
    reload();
  };

  const createSolo = async () => {
    await LeagueApi.createSoloTeam().catch(() => undefined);
    reload();
  };

  return (
    <DashboardShell>
      <div className="mx-auto max-w-3xl space-y-8 p-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          Free Agent Hub
        </h1>

        {loading ? (
          <p className="py-8 text-center text-gray-400 dark:text-gray-500">
            Loading…
          </p>
        ) : (
          <>
            <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
              <h2 className="mb-3 font-semibold text-gray-700 dark:text-gray-300">
                Your status
              </h2>
              <label
                className="flex items-center gap-2 text-sm"
                htmlFor="free-agent-toggle"
              >
                <input
                  checked={status.is_free_agent}
                  id="free-agent-toggle"
                  type="checkbox"
                  onChange={(e) =>
                    setStatus({ ...status, is_free_agent: e.target.checked })
                  }
                />
                Open to team invites (free agent)
              </label>
              <input
                className="mt-3 w-full rounded border bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
                placeholder="Short pitch (e.g. Full-stack, available weekends)"
                type="text"
                value={status.headline}
                onChange={(e) =>
                  setStatus({ ...status, headline: e.target.value })
                }
              />
              <button
                className="mt-3 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white"
                type="button"
                onClick={() => {
                  saveStatus();
                }}
              >
                Save
              </button>
            </section>

            <section>
              <h2 className="mb-3 font-semibold text-gray-700 dark:text-gray-300">
                Your invites
              </h2>
              {invites.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  No invites yet.
                </p>
              ) : (
                <ul className="space-y-2">
                  {invites.map((invite) => (
                    <li
                      key={invite.id}
                      className="flex items-center justify-between rounded border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                    >
                      <span>
                        {invite.developer_name} → {invite.team_name}{" "}
                        <span className="text-gray-400 dark:text-gray-500">
                          ({invite.status})
                        </span>
                      </span>
                      <span className="flex gap-2">
                        {(NEXT_ACTIONS[invite.status] || []).map((action) => (
                          <button
                            key={action}
                            className="rounded border px-2 py-1 text-xs capitalize text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900"
                            type="button"
                            onClick={() => {
                              advance(invite.id, action);
                            }}
                          >
                            {action}
                          </button>
                        ))}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <h2 className="mb-3 font-semibold text-gray-700 dark:text-gray-300">
                Challenges matched to you
              </h2>
              {challenges.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  No matching challenges yet.
                </p>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  {challenges.map((challenge) => (
                    <div
                      key={challenge.id}
                      className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 shadow-sm"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-800 dark:text-gray-100">
                          {challenge.name}
                        </span>
                        <span className="rounded-full bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 text-xs font-semibold text-blue-700 dark:text-blue-300">
                          {Math.round(challenge.score * 100)}% match
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {challenge.chapter}
                      </p>
                      {challenge.matched_skills.length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {challenge.matched_skills.map((skill) => (
                            <span
                              key={skill}
                              className="rounded bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-400"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section>
              <h2 className="mb-3 font-semibold text-gray-700 dark:text-gray-300">
                Free agents
              </h2>
              {agents.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  No free agents available.
                </p>
              ) : (
                <ul className="space-y-2">
                  {agents.map((agent) => (
                    <li
                      key={agent.id}
                      className="rounded border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                    >
                      <span className="font-medium">{agent.name}</span>
                      {agent.headline ? (
                        <span className="text-gray-500 dark:text-gray-400">
                          {" "}
                          — {agent.headline}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <button
                className="rounded border px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900"
                type="button"
                onClick={() => {
                  createSolo();
                }}
              >
                Create a solo team (safety valve)
              </button>
            </section>
          </>
        )}
      </div>
    </DashboardShell>
  );
}
